import { useNavigate } from 'react-router-dom';
import { Bell, Search, LogOut, Settings, User, Menu } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Sun, Moon } from 'lucide-react';
import { toast } from 'sonner';
import { openMobileSidebar } from './MobileSidebar';
import { getImageUrl } from '@/lib/imageUtils';

export const TopNav = ({ onSearchClick }) => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/auth');
    toast.success('Logged out successfully');
  };

  return (
    <header className="sticky top-0 z-50 bg-white dark:bg-zinc-900/50 border-b border-stone-300 dark:border-white/5 h-14 sm:h-16 safe-area-top">
      <div className="relative w-full px-3 sm:px-6 h-full flex items-center justify-between">
        {/* Mobile Menu Button + Logo */}
        <div className="flex items-center gap-2">
          {/* Mobile Menu Button */}
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden rounded-xl"
            onClick={openMobileSidebar}
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-5 h-5 text-stone-600 dark:text-zinc-400" strokeWidth={1.5} />
          </Button>
          
          {/* Logo */}
          <div 
            className="flex items-center cursor-pointer"
            onClick={() => navigate('/')}
            data-testid="top-nav-logo"
          >
            {/* The wordmark is a squarish 3.80:1, so it needs a taller height
                than a wide logo would to read at the same weight in the bar. */}
            <img
              src={'/logos/famcentral-logo.png'}
              alt="FamCentral"
              className="h-6 sm:h-8 w-auto"
            />
          </div>
        </div>

        {/* Centre - Empty or could add search here later */}
        <div className="flex-1" />

        {/* Right Actions */}
        <div className="flex items-center gap-1 sm:gap-2">
          {/* Search - Clickable to open Command Palette */}
          <button 
            className="hidden md:flex items-center gap-2 px-3 h-10 rounded-xl bg-stone-100 dark:bg-zinc-800/50 border border-stone-300 dark:border-white/10 text-stone-400 dark:text-zinc-500 hover:border-stone-400 dark:hover:border-zinc-600 transition-colors cursor-pointer"
            onClick={onSearchClick}
            data-testid="topnav-search"
          >
            <Search className="w-4 h-4" />
            <span className="text-sm">Search...</span>
            <div className="flex items-center gap-0.5 ml-4">
              <kbd className="px-1.5 py-0.5 bg-stone-200 dark:bg-zinc-700 rounded text-[10px] font-medium">⌘</kbd>
              <kbd className="px-1.5 py-0.5 bg-stone-200 dark:bg-zinc-700 rounded text-[10px] font-medium">K</kbd>
            </div>
          </button>

          {/* Search icon for mobile */}
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden rounded-xl"
            onClick={onSearchClick}
            data-testid="mobile-search-btn"
          >
            <Search className="w-5 h-5 text-stone-600 dark:text-zinc-400" strokeWidth={1.5} />
          </Button>

          <Button
            variant="ghost"
            size="icon"
            className="rounded-xl relative"
            data-testid="notifications-btn"
          >
            <Bell className="w-5 h-5 text-stone-600 dark:text-zinc-400" strokeWidth={1.5} />
            <span className="absolute -top-1 -right-1 w-4 h-4 sm:w-5 sm:h-5 bg-gathrr-400 text-white text-[10px] sm:text-xs rounded-full flex items-center justify-center">3</span>
          </Button>

          <Button
            variant="ghost"
            size="icon"
            className="rounded-xl hidden sm:flex"
            onClick={toggleTheme}
            data-testid="topnav-theme-toggle"
          >
            {theme === 'dark' ? (
              <Sun className="w-5 h-5 text-zinc-400" strokeWidth={1.5} />
            ) : (
              <Moon className="w-5 h-5 text-stone-600" strokeWidth={1.5} />
            )}
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Avatar
                className="w-8 h-8 sm:w-9 sm:h-9 cursor-pointer ring-2 ring-gathrr-500/20 hover:ring-gathrr-500/40 transition-all"
                data-testid="topnav-avatar"
              >
                <AvatarImage src={getImageUrl(user?.avatar_url)} className="object-cover" />
                <AvatarFallback className="bg-gathrr-100 dark:bg-gathrr-500/20 text-gathrr-700 dark:text-gathrr-400 text-sm">
                  {user?.display_name?.charAt(0)?.toUpperCase() || 'U'}
                </AvatarFallback>
              </Avatar>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 dark:bg-zinc-900 dark:border-white/10">
              <div className="px-3 py-2">
                <p className="font-semibold text-stone-800 dark:text-white">{user?.display_name}</p>
                <p className="text-sm text-stone-500 dark:text-zinc-500">@{user?.username}</p>
              </div>
              <DropdownMenuSeparator className="dark:bg-white/10" />
              <DropdownMenuItem 
                onClick={() => navigate(`/profile/${user?.username}`)}
                className="cursor-pointer"
                data-testid="menu-profile"
              >
                <User className="w-4 h-4 mr-2" />
                View Profile
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => navigate('/settings')}
                className="cursor-pointer"
                data-testid="menu-settings"
              >
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </DropdownMenuItem>
              {/* Theme toggle for mobile - in dropdown */}
              <DropdownMenuItem 
                onClick={toggleTheme}
                className="cursor-pointer sm:hidden"
              >
                {theme === 'dark' ? (
                  <>
                    <Sun className="w-4 h-4 mr-2" />
                    Light Mode
                  </>
                ) : (
                  <>
                    <Moon className="w-4 h-4 mr-2" />
                    Dark Mode
                  </>
                )}
              </DropdownMenuItem>
              <DropdownMenuSeparator className="dark:bg-white/10" />
              <DropdownMenuItem 
                onClick={handleLogout}
                className="cursor-pointer text-gathrr-600 dark:text-gathrr-400 focus:text-white"
                data-testid="menu-logout"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Log Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};
